import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  DragIndicatorOutlined as DragIcon,
  DeleteOutlined as DeleteIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTheme } from '@emotion/react';
import ReactMarkdown from 'react-markdown';

// Parse lesson content into movable blocks
const parseLessonIntoBlocks = (content) => {
  if (!content) return [];
  
  // Split by double newlines to create blocks
  const sections = content.split(/\n\s*\n/);
  const blocks = [];
  
  sections.forEach((section, index) => {
    const trimmedSection = section.trim();
    if (trimmedSection) {
      // Determine block type
      let type = 'paragraph';
      if (trimmedSection.startsWith('# ')) {
        type = 'heading1';
      } else if (trimmedSection.startsWith('## ')) {
        type = 'heading2';
      } else if (trimmedSection.startsWith('### ')) {
        type = 'heading3';
      } else if (trimmedSection.match(/^[-*•○]\s/) || trimmedSection.match(/^\d+\.\s/)) {
        type = 'list';
      }
      
      blocks.push({
        id: `block-${index}`,
        type,
        content: trimmedSection,
        isUserAdded: false, // Track if this was added by the user via chat
      });
    }
  });
  
  return blocks;
};

// Individual draggable block component - Notion-style
const DraggableBlock = React.memo(({ block, onDelete, markdownComponents }) => {
  const theme = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        position: 'relative',
        py: 1,
        px: 0,
        cursor: isDragging ? 'grabbing' : 'default',
        // Subtle highlight for user-added blocks
        backgroundColor: block.isUserAdded ? theme.palette.primary.main + '08' : 'transparent',
        borderRadius: block.isUserAdded ? '4px' : 0,
        '&:hover': {
          '& .drag-handle': {
            opacity: 1,
          },
          '& .delete-button': {
            opacity: block.isUserAdded ? 1 : 0,
          },
        },
      }}
    >
      {/* Drag Handle - Notion-style dots */}
      <Tooltip title="Drag to move" placement="left">
        <Box
          className="drag-handle"
          {...attributes}
          {...listeners}
          sx={{
            position: 'absolute',
            left: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            opacity: 0,
            transition: 'opacity 0.2s ease',
            cursor: 'grab',
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '2px',
            zIndex: 1,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1px',
            }}
          >
            {/* Two rows of dots to create ⋮⋮ pattern */}
            <Box sx={{ display: 'flex', gap: '1px' }}>
              <Box
                sx={{
                  width: 2,
                  height: 2,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.text.secondary,
                }}
              />
              <Box
                sx={{
                  width: 2,
                  height: 2,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.text.secondary,
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: '1px' }}>
              <Box
                sx={{
                  width: 2,
                  height: 2,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.text.secondary,
                }}
              />
              <Box
                sx={{
                  width: 2,
                  height: 2,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.text.secondary,
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: '1px' }}>
              <Box
                sx={{
                  width: 2,
                  height: 2,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.text.secondary,
                }}
              />
              <Box
                sx={{
                  width: 2,
                  height: 2,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.text.secondary,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Tooltip>

      {/* Delete Button for user-added blocks */}
      {block.isUserAdded && (
        <Tooltip title="Remove block">
          <IconButton
            className="delete-button"
            onClick={() => onDelete(block.id)}
            sx={{
              position: 'absolute',
              right: -8,
              top: 8,
              opacity: 0,
              transition: 'opacity 0.2s ease',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.secondary,
              width: 20,
              height: 20,
              '&:hover': {
                backgroundColor: theme.palette.error.main,
                color: theme.palette.error.contrastText,
                borderColor: theme.palette.error.main,
              },
            }}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Content - no card wrapper */}
      <Box sx={{ pl: 2, pr: block.isUserAdded ? 3 : 0 }}>
        <ReactMarkdown components={markdownComponents}>
          {block.content}
        </ReactMarkdown>
      </Box>
    </Box>
  );
});

// Main draggable content component
const DraggableLessonContent = React.forwardRef((props, ref) => {
  const { 
    lessonContent, 
    setLessonContent, 
    markdownComponents,
    highlightedChanges = [],
    setHighlightedChanges 
  } = props;
  const [blocks, setBlocks] = useState([]);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Parse lesson content into blocks when it changes
  useEffect(() => {
    const newBlocks = parseLessonIntoBlocks(lessonContent);
    setBlocks(newBlocks);
  }, [lessonContent]);

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setBlocks((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        const newBlocks = arrayMove(items, oldIndex, newIndex);
        
        // Update the lesson content
        const newContent = newBlocks
          .map(block => block.content)
          .join('\n\n');
        setLessonContent(newContent);
        
        return newBlocks;
      });
    }
  };

  // Handle block deletion
  const handleDeleteBlock = (blockId) => {
    setBlocks((items) => {
      const newBlocks = items.filter(block => block.id !== blockId);
      
      // Update the lesson content
      const newContent = newBlocks
        .map(block => block.content)
        .join('\n\n');
      setLessonContent(newContent);
      
      return newBlocks;
    });
  };

  // Add new block (called from outside when user adds via chat)
  const addBlock = (content, position = 'end') => {
    const newBlock = {
      id: `user-block-${Date.now()}`,
      type: 'paragraph',
      content,
      isUserAdded: true,
    };

    setBlocks((items) => {
      let newBlocks;
      if (position === 'end') {
        newBlocks = [...items, newBlock];
      } else if (typeof position === 'number') {
        newBlocks = [...items];
        newBlocks.splice(position, 0, newBlock);
      } else {
        newBlocks = [...items, newBlock];
      }
      
      // Update the lesson content
      const newContent = newBlocks
        .map(block => block.content)
        .join('\n\n');
      setLessonContent(newContent);
      
      return newBlocks;
    });

    // Highlight the new block briefly
    if (setHighlightedChanges) {
      setHighlightedChanges([{ type: 'added', content }]);
      setTimeout(() => setHighlightedChanges([]), 3000);
    }
  };

  // Expose addBlock function to parent component
  React.useImperativeHandle(ref, () => ({
    addBlock,
  }));

  if (blocks.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No content to display
      </Typography>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={blocks} strategy={verticalListSortingStrategy}>
        <Box sx={{ position: 'relative' }}>
          {blocks.map((block) => (
            <DraggableBlock
              key={block.id}
              block={block}
              onDelete={handleDeleteBlock}
              markdownComponents={markdownComponents}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
});

DraggableLessonContent.displayName = 'DraggableLessonContent';

export default DraggableLessonContent;